import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Client } from "@/models/Client";

interface ClientRemainingAmount {
  clientId: string;
  totalSubscriptionAmount: number;
  totalPaidAmount: number;
  remainingAmount: number;
}

export const useClientRemainingAmounts = (clients: Client[]) => {
  return useQuery({
    queryKey: ['clientRemainingAmounts', clients.map(c => c.id).sort().join(',')],
    queryFn: async (): Promise<Record<string, ClientRemainingAmount>> => {
      if (clients.length === 0) return {};

      const results: Record<string, ClientRemainingAmount> = {};

      // Process each client to calculate remaining amounts
      await Promise.all(
        clients.map(async (client) => {
          // Calculate total subscription amount from active subscriptions
          const totalSubscriptionAmount = client.subscriptions
            ?.filter(sub => sub.isActive)
            ?.reduce((sum, sub) => sum + sub.price, 0) || 0;

          // Get all payments for this client using the existing database function
          const { data: payments, error } = await supabase.rpc(
            'get_client_payments',
            { p_client_id: client.id }
          );

          let totalPaidAmount = 0;
          if (!error && payments) {
            // Sum all payments made by this client
            totalPaidAmount = payments.reduce((sum: number, payment: any) => sum + (payment.amount || 0), 0);
          }

          // Calculate remaining amount
          const remainingAmount = Math.max(0, totalSubscriptionAmount - totalPaidAmount);

          results[client.id] = {
            clientId: client.id,
            totalSubscriptionAmount,
            totalPaidAmount,
            remainingAmount
          };
        })
      );

      return results;
    },
    enabled: clients.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};